'use client'

import { useCallback, useEffect, useState, useRef } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { RemoveScroll } from 'react-remove-scroll'
import { Box, IconButton } from '@mui/material'
import Menu from '@mui/icons-material/Menu'
import Close from '@mui/icons-material/Close'
import MobileMenu from '../mobileMenu/mobileMenu'
import { useGlobalState } from '@biolumina/ui/context/globalStateProvider'
import { StyledMobileNav } from './mobileNav.styles'

export default function MobileNav() {
  const router = useRouter()
  const pathname = usePathname()
  const {
    siteData: { nav, topLinks },
  } = useGlobalState()
  const [current, setCurrent] = useState(null)
  const [mobileMenuOn, setMobileMenuOn] = useState(false)
  const { scrollMargin } = useGlobalState()

  const hasOpened = useRef(false)
  const prevUrl = useRef('')

  const closeMenu = useCallback(() => {
    setMobileMenuOn(false)
  }, [])

  const onNavToggle = useCallback(() => {
    const willOpen = !mobileMenuOn
    setMobileMenuOn(willOpen)
    
    if (willOpen) {
      hasOpened.current = true
      prevUrl.current = window.location.href
    }
  }, [mobileMenuOn])

  const onMenuToggle = useCallback((href) => {
    setCurrent((val) => (val === href ? null : href))
  }, [])
  
  const onMenuInit = useCallback((href) => {
    setCurrent(href)
  }, [])

  // Close when navigating to a different page
  useEffect(() => {
    closeMenu()
  }, [pathname, closeMenu])

  // Close on hash change (same page)
  useEffect(() => {
    const handleHashChange = () => {
      closeMenu()
    }
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [closeMenu])

  // URL monitoring — only active after user has opened the menu
  useEffect(() => {
    if (!mobileMenuOn || !hasOpened.current) return

    const checkUrlChange = () => {
      const currentUrl = window.location.href
      if (currentUrl !== prevUrl.current) {
        closeMenu()
        prevUrl.current = currentUrl
      }
    }

    const interval = setInterval(checkUrlChange, 80)
    return () => clearInterval(interval)
  }, [mobileMenuOn, closeMenu])

  if (nav?.length <= 0) return null

  return (
    <StyledMobileNav scrollMargin={scrollMargin}>
      <IconButton
        disableRipple
        color="primary"
        onClick={onNavToggle}
        aria-label={mobileMenuOn ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={mobileMenuOn}
        aria-controls="mobile-nav-menu"
      >
        {mobileMenuOn ? <Close sx={{ fontSize: '45px' }} /> : <Menu sx={{ fontSize: '45px' }} />}
      </IconButton>

      {mobileMenuOn && (
        <RemoveScroll>
          <Box className="menu-wrapper">
            <Box className="backdrop" onClick={closeMenu} />
            {nav.map((navItem) => (
              <MobileMenu
                onInit={onMenuInit}
                isOpen={navItem.href === current}
                onToggle={onMenuToggle}
                onNavToggle={closeMenu}
                key={`${navItem.href}-menu`}
                data={navItem}
              />
            ))}
            <MobileMenu data={topLinks[3]} onNavToggle={closeMenu} />
          </Box>
        </RemoveScroll>
      )}
    </StyledMobileNav>
  )
}